import { Injectable, OnDestroy } from '@angular/core';
import { UnleashClient } from 'unleash-proxy-client';
import { BehaviorSubject, EMPTY, filter, Observable, of, Subscription, take, takeUntil, timer } from 'rxjs';
import { catchError, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { ConfigurationService } from './configuration.service';
import { ProfileService } from './show/profile.service';
import { UserService } from './api/auth/user.service';
import { LOCAL_STORAGE_INITIAL_URL_PARAMS_KEY } from '../constants/shared/local-storage.constants';
import { GuestService } from './guest.service';
export type PaymentVariantNames = 'trial' | 'immediate';
export interface IResentedThresholdVariant {
    timeout: number;
    count: number;
}
export type PaymentFeatureFlagEvents = 'should_see_paywall' | 'ability_to_close_paywall' | 'paywall_timeout';
export type ExportFeatureFlagEvents = 'editroom_experiment_paywall';
export type ExportVariantsNames = 'watermark' | 'paywall' | 'none';
export type FeatureFlashEvents =
    | 'shuffllTeamFeature'
    | 'Editroom_access'
    | 'wizard_videotype_selection'
    | 'wizard_confirm_finish'
    | 'randomize_videotype_wizard'
    | 'randomize_videotype_banner'
    | 'randomize_onboarding_questions'
    | 'randomize_ai_voice'
    | 'display_embed_button'
    | 'should_see_api'
    | 'cancellation_reason'
    | 'display_create_org_button'
    | 'display_avatar_project_type'
    | 'display_project_language_pick'
    | PaymentFeatureFlagEvents
    | 'clean_login_page'
    | 'authorized_domains'
    | 'display_theme_toggle'
    | 'display_fonts'
    | 'navbar_hidden'
    | 'breadcrumbs_hidden'
    | 'invite_for_project_hidden'
    | 'paywall_bypass'
    | 'display_translations'
    | 'display_creatify'
    | 'display_heygen'
    | 'generate_video_hidden'
    | 'display_openai_voice'
    | 'display_elevenlabs_voice'
    | 'video_in_ai_voice'
    | 'display_extend_video'
    | 'display_include_logo_in_generate_image'
    | 'listing-to-vid-default-template';

@Injectable({
    providedIn: 'root',
})
export class FeatureService implements OnDestroy {
    private unleash: UnleashClient;
    private featureCache: Map<string, BehaviorSubject<boolean>> = new Map();
    private userSubscription: Subscription;
    private featureFlagChange$ = new BehaviorSubject<void>(null);
    private updatingContextPromise: Promise<void>;

    private _unleashInitializedSubject = new BehaviorSubject<boolean>(false);
    public unleashInitialized$ = this._unleashInitializedSubject.asObservable();

    /**
     * Max time to wait for Unleash to start before letting the app proceed.
     * All feature getters fall back to safe defaults, so a slow/blocked/down
     * Unleash must never block the app (e.g. leaving the login page stuck on
     * "Signing In..." forever).
     */
    private static readonly UNLEASH_INIT_TIMEOUT_MS = 5000;

    /**
     * Per-request timeout for Unleash toggle fetches. The unleash-proxy-client
     * has no fetch timeout of its own, so a stalled request (ad-blocker, flaky
     * network) hangs forever and the periodic refresh loop never arms. Aborting
     * a stalled fetch turns the hang into a fast error, which lets the client's
     * refresh interval kick in and self-heal once the network recovers.
     */
    private static readonly UNLEASH_FETCH_TIMEOUT_MS = 8000;

    constructor(
        private config: ConfigurationService,
        private profileService: ProfileService,
        private userService: UserService,
        private guestService: GuestService
    ) {
        this.unleash = new UnleashClient({
            ...config.unleashConfig,
            // Give every toggle fetch an abort-timeout so a stalled request can't
            // hang the client indefinitely (see UNLEASH_FETCH_TIMEOUT_MS).
            createAbortController: () => this.createTimedAbortController(),
        });
        this.subscribeToUserChanges();
        this.initUnleashAsync();
    }

    private createTimedAbortController(): AbortController {
        const controller = new AbortController();
        setTimeout(() => controller.abort(), FeatureService.UNLEASH_FETCH_TIMEOUT_MS);
        return controller;
    }
    private async initUnleashAsync() {
        try {
            // unleash.start() awaits an initial network fetch that has no timeout
            // of its own. If that request hangs (ad-blocker, offline, host
            // unreachable) start() never resolves. Race it against a timeout so
            // the app always proceeds; feature flags will populate later if/when
            // the network recovers.
            await Promise.race([
                this.unleash.start(),
                new Promise<void>((resolve) => setTimeout(resolve, FeatureService.UNLEASH_INIT_TIMEOUT_MS)),
            ]);
        } catch (error) {
            console.error('Unleash failed to initialize; continuing with default feature flags', error);
        } finally {
            this._unleashInitializedSubject.next(true);
        }
    }
    /// using or string because we have in our html something dynamic related to item + 'Feature'
    hasFeatureAccess(feature: FeatureFlashEvents | string): Observable<boolean> {
        if (!this.featureCache.has(feature)) {
            const subject = new BehaviorSubject<boolean>(null);
            this.featureCache.set(feature, subject);
            this.updateFeatureFlag(feature);
        }
        return this.featureCache.get(feature).asObservable();
    }

    getFeatureAccessValue(feature: FeatureFlashEvents | string) {
        return new Promise<boolean>((resolve, reject) =>
            this.hasFeatureAccess(feature)
                .pipe(
                    filter((value) => value !== null),
                    take(1)
                )
                .subscribe((value) => {
                    return resolve(value);
                })
        );
    }

    ngOnDestroy(): void {
        if (this.userSubscription) {
            this.userSubscription.unsubscribe();
        }
    }

    public async getCleanLoginPageAsync() {
        try {
            const isCleanLoginPage = await this.getFeatureAccessValue('clean_login_page');
            return isCleanLoginPage;
        } catch (err) {
            console.error(`Could not get clean login page`, err);
            return false;
        }
    }
    public async getAuthorizedDomainsAsync(): Promise<string[]> {
        try {
            const paymentFlag: FeatureFlashEvents = 'authorized_domains';
            const variant = this.unleash.getVariant(paymentFlag);
            const authorizedDomains = JSON.parse(variant.payload.value) as string[];
            return authorizedDomains;
        } catch (err) {
            console.error(`Could not get authorized domains`, err);
            return [];
        }
    }

    private subscribeToUserChanges(): void {
        this.userSubscription = this.profileService.user$
            .pipe(
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
                catchError((error) => {
                    console.error('Error in user subscription:', error);
                    return EMPTY;
                })
            )
            .subscribe(async (user) => {
                this.handleUserContextUpdate();
            });
    }

    private updateFeatureFlag(feature: string, forceUpdate = false): void {
        timer(0, this.config.unleashConfig.cacheInterval)
            .pipe(
                switchMap(() => this.checkFeature(feature)),
                catchError((error) => {
                    console.error(`Error checking feature ${feature}:`, error);
                    return of(false);
                })
            )
            .subscribe((isEnabled) => {
                if (this.featureCache.has(feature)) {
                    this.featureCache.get(feature).next(isEnabled);
                }
            });
    }

    public getVariant(flagName: FeatureFlashEvents) {
        return this.unleash.isEnabled(flagName);
    }

    public getPaymentVariant() {
        const paymentFlag: PaymentFeatureFlagEvents = 'should_see_paywall';
        const variant = this.unleash.getVariant(paymentFlag);
        return {
            name: variant.name as PaymentVariantNames,
        };
    }
    public getResendThresholdVariant() {
        const flag = 'resend_invite_guest_timeout';
        const variant = this.unleash.getVariant(flag);
        return JSON.parse(variant.payload.value) as IResentedThresholdVariant;
    }
    public getExportBehavior(): ExportVariantsNames {
        const editRoomFlag: ExportFeatureFlagEvents = 'editroom_experiment_paywall';
        const variant = this.unleash.getVariant(editRoomFlag);

        if (!variant.enabled) {
            return 'none';
        }
        if (variant.name === 'watermark') {
            return 'watermark';
        }
        if (variant.name === 'paywall') {
            return 'paywall';
        }
        return 'none';
    }

    public getPaywallTimeout(): number {
        const timeoutFlag: PaymentFeatureFlagEvents = 'paywall_timeout';
        const variant = this.unleash.getVariant(timeoutFlag);
        if (!variant) {
            return 4000;
        }
        if (variant.payload.type === 'number') {
            return Number(variant.payload.value);
        }
    }

    private checkFeature(feature: string): Observable<boolean> {
        try {
            const isEnabled = this.unleash.isEnabled(feature);
            return of(isEnabled);
        } catch (error) {
            console.error(`Error checking feature ${feature}:`, error);
            return of(false);
        }
    }

    public async checkFeatureFromUnleashAsync(feature: string) {
        if (this.updatingContextPromise) {
            await this.updatingContextPromise;
        }
        return this.unleash.isEnabled(feature);
    }

    get featureFlagChange(): Observable<void> {
        return this.featureFlagChange$.asObservable();
    }

    private invalidateCache() {
        this.featureCache.forEach((_, feature) => this.forceUpdateFeatureFlag(feature));
        this.featureFlagChange$.next();
    }

    getInitialUrlParams() {
        try {
            /// Get all initial URl Params from local storage
            const localStorageInitialUrlParams = localStorage.getItem(LOCAL_STORAGE_INITIAL_URL_PARAMS_KEY);

            if (localStorageInitialUrlParams) {
                const urlParams = JSON.parse(localStorageInitialUrlParams);
                const urlParamContext = {};
                // Constract an object in this form:
                // urlparam.key = urlparam.value
                for (const key in urlParams) {
                    const value = urlParams[key];
                    if (value) {
                        urlParamContext['urlParams.' + key] = value;
                    }
                }
                return urlParamContext;
            }
            return null;
        } catch (ex) {
            console.error(ex);
            return null;
        }
    }

    /**
     *  Forces an update of a feature flag from unleash.
     * @param feature
     * @private
     */
    private forceUpdateFeatureFlag(feature: string) {
        if (!this.featureCache.has(feature)) {
            this.featureCache.set(feature, new BehaviorSubject<boolean>(false));
        }
        this.featureCache.get(feature).next(this.unleash.isEnabled(feature));
    }

    public handleUserContextUpdate() {
        const user = this.profileService.user$.value;
        if (!user) {
            this.unleash.updateContext({});
            this.invalidateCache();
            return;
        }
        try {
            const isMobile = this.userService.isMobileUser();
            const userDevice = isMobile === true ? 'mobile' : 'desktop';
            const userBrowser = this.profileService.userBrowser;

            let properties = {
                'user._id': user._id,
                'user.email': user.email,
                'user.plan': user.plan,
                'user.device': userDevice || 'unknown',
                'user.browser': userBrowser || 'unknown',
                'user.isGuest': this.guestService.getGuest().toString(),
                companySize: (user.branding?.company?.minSize ?? 0).toString(),
                companyGeoLocation: user.branding?.company?.geoLocation,
            };

            const urlParams = this.getInitialUrlParams();
            if (urlParams) {
                properties = { ...properties, ...urlParams };
            }

            if (user.ft && user.ft.length > 0) {
                properties['user.ft'] = user.ft.join(',');
            }

            this.updatingContextPromise = this.unleash.updateContext({
                properties,
                userId: user.email,
            });

            this.invalidateCache();
        } catch (error) {
            console.error('Error updating Unleash context:', error);
        }
    }
}
